import { useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";

import { FormControlWrapper } from "components/Shared";
import { ComboBox } from "components/Shared/Inputs";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";
import { useRiskAssessmentActions } from "./hooks/useRiskAssessmentActions";
import { ParticipantAccordion } from "./components/ParticipantAccordion";
import { PARTICIPANT_NUMBER_OPTIONS } from "./RiskAssessments.constants";

import "./RiskAssessments.scss";

export const RiskAssessments: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation("criticalIncident", { useSuspense: false });
  const [expandedParticipantIndex, setExpandedParticipantIndex] = useState<number | false>(-1);

  const {
    isReadonly,
    participants,
    wereThereAnyAtRiskIndividuals,
    noOfParticipants,
    riskAssessmentTouched,
    riskAssessmentErrors,
    handleAtRiskChange,
    handleNumberOfParticipantsChange,
    handleRemoveParticipant,
    patchParticipant,
  } = useRiskAssessmentActions();

  return translationsReady && (
    <div className="risk-assessments">
      <h2>{t('riskAssessment')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="radio.yesno"
            label={`${t("were_there_any_at_risk_individuals")}*`}
            name="riskAssessment.wereThereAnyAtRiskIndividuals"
            fullWidth
            error={riskAssessmentTouched?.wereThereAnyAtRiskIndividuals && Boolean(riskAssessmentErrors?.wereThereAnyAtRiskIndividuals)}
            helperText={riskAssessmentTouched?.wereThereAnyAtRiskIndividuals ? t(riskAssessmentErrors?.wereThereAnyAtRiskIndividuals || " ") : ""}
          >
            <ChooseYesNo
              label={`${t("were_there_any_at_risk_individuals")}*`}
              name="riskAssessment.wereThereAnyAtRiskIndividuals"
              value={wereThereAnyAtRiskIndividuals}
              onChange={(value) => handleAtRiskChange(Boolean(value))}
            />
          </FormControlWrapper>
        </Grid>

        {wereThereAnyAtRiskIndividuals && (
          <>
            <Grid item sm={12}>
              <FormControlWrapper
                isReadonly={isReadonly}
                type="select"
                fullWidth
                label={t("number_of_participants")}
                name="riskAssessment.noOfParticipants"
                error={riskAssessmentTouched?.noOfParticipants && Boolean(riskAssessmentErrors?.noOfParticipants)}
                helperText={riskAssessmentTouched?.noOfParticipants ? t(riskAssessmentErrors?.noOfParticipants || " ") : ""}
                options={PARTICIPANT_NUMBER_OPTIONS.map((n) => ({ label: `${n}`, value: `${n}` }))}
              >
                <ComboBox
                  name="riskAssessment.noOfParticipants"
                  required
                  options={PARTICIPANT_NUMBER_OPTIONS}
                  getOptionLabel={(option) => `${option}`}
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionDisabled={(option) => option <= participants.length && option !== noOfParticipants}
                  disableClearable
                  value={noOfParticipants}
                  onChange={(_event, newValue) => newValue !== null && handleNumberOfParticipantsChange(newValue)}
                />
              </FormControlWrapper>
            </Grid>

            {participants.map((participant, index) => (
              <Grid item sm={12} key={index}>
                <ParticipantAccordion
                  index={index}
                  participant={participant}
                  isReadonly={isReadonly}
                  removeAction={() => handleRemoveParticipant(index)}
                  removeHidden={participants.length <= 1}
                  patchParticipant={(partial) => patchParticipant(index, partial)}
                  expanded={expandedParticipantIndex === index}
                  onToggle={(isExpanded) => setExpandedParticipantIndex(isExpanded ? index : false)}
                />
              </Grid>
            ))}
          </>
        )}
      </Grid>
    </div>
  );
};
